import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase, type FacebookGroup } from "@/lib/supabaseClient";

const TYPE_COLORS: Record<string, string> = {
  "Public": "bg-green-50 text-green-700",
  "Private": "bg-amber-50 text-amber-700",
  "Buy/Sell/Trade": "bg-rose-50 text-rose-700",
  "Swap Group": "bg-[#f5ede0] text-[#8b5e3c]",
  "Educational": "bg-sky-50 text-sky-700",
  "Inspiration": "bg-violet-50 text-violet-700",
};

export default function FacebookGroupsSection() {
  const [groups, setGroups] = useState<FacebookGroup[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGroups = async () => {
      const { data, error } = await supabase
        .from("facebook_groups")
        .select("*")
        .eq("approved", true)
        .order("featured", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(6);
      if (!error && data) setGroups(data as FacebookGroup[]);
      setLoading(false);
    };
    fetchGroups();
  }, []);

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f5ede0] text-[#8b5e3c] text-xs font-semibold uppercase tracking-widest mb-4">
              <i className="ri-facebook-circle-line"></i>
              Community Groups
            </div>
            <h2
              className="text-4xl font-bold text-[#2c1e0f]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Find Your Community
            </h2>
            <p className="text-[#7a6555] mt-2 max-w-lg">
              Join thousands of paper crafters in these active Facebook groups — swap ephemera,
              share inspiration, and connect with fellow junk journalers.
            </p>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Link
              to="/groups/submit"
              className="px-4 py-2 rounded-full border border-[#c9a87c] text-[#8b5e3c] text-sm font-medium hover:bg-[#f5ede0] transition-colors whitespace-nowrap"
            >
              List Your Group
            </Link>
            <Link
              to="/groups"
              className="flex items-center gap-1.5 text-sm font-medium text-[#8b5e3c] hover:text-[#7a5234] transition-colors whitespace-nowrap"
            >
              Browse all groups
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </div>

        {/* Group Cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-2xl bg-[#faf8f4] border border-[#ede5d8] h-52 animate-pulse" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#ede5d8] py-16 text-center">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-[#f5ede0] text-[#c9a87c]">
              <i className="ri-group-line text-2xl"></i>
            </div>
            <h3 className="font-bold text-[#2c1e0f] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              No groups listed yet
            </h3>
            <p className="text-sm text-[#7a6555] mb-5">Be the first to list your Facebook group!</p>
            <Link
              to="/groups/submit"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#8b5e3c] text-white text-sm font-semibold hover:bg-[#7a5234] transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line"></i> List Your Group
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {groups.map((group) => (
              <GroupCard key={group.id} group={group} />
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        {groups.length > 0 && (
          <div className="mt-10 text-center">
            <Link
              to="/groups"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#c9a87c] text-[#8b5e3c] font-semibold text-sm hover:bg-[#f5ede0] transition-colors whitespace-nowrap"
            >
              View All Groups
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

function GroupCard({ group }: { group: FacebookGroup }) {
  const topics = (group.topics || "").split(",").map((t) => t.trim()).filter(Boolean);

  return (
    <div className="bg-[#faf8f4] rounded-2xl border border-[#ede5d8] overflow-hidden flex flex-col hover:border-[#c9a87c] transition-all duration-300 hover:-translate-y-0.5 group">
      {/* Banner */}
      <div className="relative h-32 bg-[#f5ede0] overflow-hidden">
        {group.image_url ? (
          <img
            src={group.image_url}
            alt={group.group_name}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white/70 text-[#c9a87c]">
              <i className="ri-facebook-circle-fill text-3xl"></i>
            </div>
          </div>
        )}
        {group.featured && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f0c87a] text-[#2c1e0f] text-xs font-bold">
            <i className="ri-star-fill text-xs"></i> Featured
          </div>
        )}
        {group.group_type && (
          <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-semibold ${TYPE_COLORS[group.group_type] || "bg-white/80 text-[#3a2e1e]"}`}>
            {group.group_type}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4 flex flex-col flex-1">
        <h3
          className="font-bold text-[#2c1e0f] text-sm mb-0.5 line-clamp-1"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {group.group_name}
        </h3>

        <div className="flex items-center gap-3 mb-2">
          {group.member_count && (
            <span className="text-xs text-[#8b5e3c] font-semibold flex items-center gap-1">
              <i className="ri-group-line text-xs"></i>{group.member_count} members
            </span>
          )}
          {group.location && (
            <span className="text-xs text-[#9a8a78] flex items-center gap-1">
              <i className="ri-map-pin-line text-xs"></i>{group.location}
            </span>
          )}
        </div>

        {group.description && (
          <p className="text-xs text-[#6b5a4a] leading-relaxed line-clamp-2 mb-3 flex-1">
            {group.description}
          </p>
        )}

        {topics.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {topics.slice(0, 2).map((t) => (
              <span key={t} className="px-2 py-0.5 rounded-full bg-white border border-[#ede5d8] text-[#8b5e3c] text-xs">
                {t}
              </span>
            ))}
            {topics.length > 2 && (
              <span className="px-2 py-0.5 rounded-full bg-white border border-[#ede5d8] text-[#9a8a78] text-xs">
                +{topics.length - 2}
              </span>
            )}
          </div>
        )}

        <a
          href={group.facebook_url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-[#1877F2]/10 text-[#1877F2] text-xs font-semibold hover:bg-[#1877F2]/20 transition-colors whitespace-nowrap cursor-pointer mt-auto"
        >
          <i className="ri-facebook-circle-fill text-sm"></i>
          Join on Facebook
        </a>
      </div>
    </div>
  );
}
